import { Injector } from "@angular/core";
import { AuthService } from "src/app/services/auth/auth.service";

export abstract class Model {
    attributes: { [key: string]: any } = {};
    public translatable: string[] = [];
    public strictTranslations: boolean = false;
    private static injector: Injector;

    constructor(item) {
        Object.assign(this.attributes, item);
        return new Proxy(this, this);
    }

    static setInjector(injector: Injector): void {
        Model.injector = injector;
    }

    private get authService(): AuthService {
        if (!Model.injector) {
            throw new Error(
                "Injector not set. Call Model.setInjector() in your app initialization."
            );
        }
        return Model.injector.get(AuthService);
    }

    get(target: any, key: string) {
        // Check if it's a method on the target object first
        if (typeof target[key] === "function") {
            return target[key].bind(target);
        }

        if (this.translatable.includes(key)) {
            const authUser = this.authService.authState.getValue().authUser;
            const languages = authUser.getLanguagePriorityArray();
            const translations = target.attributes.translations;
            let translationData = target.attributes[key];
            if (Array.isArray(translations)) {
                translationData = translations?.reduce((acc, translation) => {
                    acc[translation.language] = translation.content;
                    return acc;
                }, {});
            }

            if (!translationData || typeof translationData !== "object") {
                return translationData;
            }

            if (this.strictTranslations) {
                return translationData[languages[0]] || null;
            }

            for (const language of languages) {
                if (translationData[language]) {
                    return translationData[language];
                }
            }

            // Fallback to first non-empty value
            const values = Object.values(translationData);
            return values.find((val) => val) || values[0] || null;
        }

        // Check if property exists directly on target (set after super())
        if (key in target) {
            return target[key];
        }

        return target.attributes[key];
    }

    set(target: any, key: string, value: any) {
        if (Object.hasOwnProperty.call(target, key)) {
            return (target[key] = value);
        }

        if (this.translatable.includes(key)) {
            const authUser = this.authService.authState.getValue().authUser;
            const languages = authUser.getLanguagePriorityArray();

            target.attributes[key] = {
                ...target.attributes[key],
                [languages[0]]: value
            };
            return true;
        }

        target.attributes[key] = value;
        return true;
    }
}
